import { computed, type Ref } from "vue";
import type {
  PaymentMethod,
  SelectablePaymentOptions,
  PayabliConfig,
} from "@/types";
import {
  getPayabliCardConfig,
  PAYABLI_TYPE_METHOD_EMBEDDED,
  PAYABLI_ROOT_CONTAINER,
  PAYABLI_DEFAULT_OPEN_CARD,
  PAYABLI_DEFAULT_OPEN_ACH,
} from "../constants";
import { BASE_URL } from "@/shared/utils/env";

export interface UsePayabliConfigOptions {
  apiToken: string;
  entryPointName: string;
  customerNumber: string;
  selectedPaymentMethod: Ref<PaymentMethod>;
  createTemporaryPaymentMethod: Ref<boolean>;
  onSuccess: (
    referenceId: string,
    referenceType: PaymentMethod,
    paymentOption: SelectablePaymentOptions
  ) => Promise<void>;
  onReady: (isValid: boolean) => void;
  onError: (errors: any) => void;
}

export const usePayabliConfig = (options: UsePayabliConfigOptions) => {
  const {
    apiToken,
    entryPointName,
    customerNumber,
    selectedPaymentMethod,
    createTemporaryPaymentMethod,
    onSuccess,
    onReady,
    onError,
  } = options;

  const showingCreditCardPaymentOption = computed(
    () => selectedPaymentMethod.value !== PAYABLI_DEFAULT_OPEN_ACH
  );

  const createConfig = (
    selectedPaymentOption: Ref<SelectablePaymentOptions>
  ): PayabliConfig => {
    return {
      type: PAYABLI_TYPE_METHOD_EMBEDDED,
      rootContainer: PAYABLI_ROOT_CONTAINER,
      defaultOpen: selectedPaymentMethod.value,
      token: apiToken,
      entryPoint: entryPointName,
      temporaryToken: createTemporaryPaymentMethod.value,
      customCssUrl: BASE_URL ? `${BASE_URL}/payabli.css` : undefined,
      card: getPayabliCardConfig(showingCreditCardPaymentOption.value),
      customerData: {
        customerNumber: customerNumber,
      },
      functionCallBackSuccess: async (response: any) => {
        const data = response.responseData;
        const { referenceId } = data;
        const referenceType = selectedPaymentMethod.value;
        await onSuccess(
          referenceId,
          referenceType,
          selectedPaymentOption.value
        );
      },
      functionCallBackReady: (data: any) => {
        // This checks that all of the fields are populated + valid
        onReady(data[1] === true);
      },
      functionCallBackError: (errors: any) => {
        onError(errors);
      },
    };
  };

  const updateConfig = (
    baseConfig: PayabliConfig,
    ccShowing: boolean
  ): PayabliConfig => {
    return {
      ...baseConfig,
      card: getPayabliCardConfig(ccShowing),
      defaultOpen: ccShowing
        ? PAYABLI_DEFAULT_OPEN_CARD
        : PAYABLI_DEFAULT_OPEN_ACH,
    };
  };

  return {
    createConfig,
    updateConfig,
    showingCreditCardPaymentOption,
  };
};
